import { useState } from 'react';
import { Box, Typography, Popover, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';

import { useWidgetFilters } from '../hooks/useWidgetFilters';
import { useIsCustomDashboard } from '../hooks/useIsCustomDashboard';

export default function WidgetAppliedFilters() {
  const { t } = useTranslation();
  const {
    widgetAppliedFilters: {
      count: filterCount,
      unappliedFilterLabels,
      appliedFilterLabels,
    },
    isWidgetLocked,
  } = useWidgetFilters();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  // Only show applied filters for custom dashboards
  const isCustomDashboard = useIsCustomDashboard();
  if (!isCustomDashboard) {
    return null;
  }

  // Don't render anything if there are no unapplied filters and widget is not locked
  if (filterCount === 0 && !isWidgetLocked) {
    return null;
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Format the filter labels
  const unappliedText = unappliedFilterLabels.join(', ');
  const appliedText = appliedFilterLabels.join(', ');

  // Determine singular or plural
  const isPlural = unappliedFilterLabels.length > 1;

  // Build the message with bold filter labels
  const renderMessage = () => {
    // If widget is locked, show a message about custom parameters
    if (isWidgetLocked) {
      return (
        <>
          {t('blox.dashboards.widgets.widget_has_custom_parameters', {
            defaultValue: 'This widget is configured to specific filters.',
          })}{' '}
          {t('blox.dashboards.widgets.dashboard_filters_may_not_apply', {
            defaultValue: 'Dashboard filters will not apply.',
          })}
        </>
      );
    }

    // Standard message for filters that don't apply
    const filterWord = isPlural
      ? t('blox.dashboards.widgets.filters', { defaultValue: 'filters' })
      : t('blox.dashboards.widgets.filter', { defaultValue: 'filter' });

    return (
      <>
        {t('blox.dashboards.widgets.the', { defaultValue: 'The' })}{' '}
        <strong>{unappliedText}</strong> {filterWord}{' '}
        {isPlural
          ? t('blox.dashboards.widgets.do_not_apply', { defaultValue: 'do not apply' })
          : t('blox.dashboards.widgets.does_not_apply', {
              defaultValue: 'does not apply',
            })}{' '}
        {t('blox.dashboards.widgets.to_this_widget', { defaultValue: 'to this widget.' })}
        {appliedFilterLabels.length > 0 ? (
          <>
            {' '}
            <strong>{appliedText}</strong>{' '}
            {appliedFilterLabels.length > 1
              ? t('blox.dashboards.widgets.filters_still_apply', {
                  defaultValue: 'filters still apply.',
                })
              : t('blox.dashboards.widgets.filter_still_applies', {
                  defaultValue: 'filter still applies.',
                })}
          </>
        ) : (
          <>
            {' '}
            {t('blox.dashboards.widgets.all_other_filters_applied', {
              defaultValue: 'All other filters have been applied.',
            })}
          </>
        )}
      </>
    );
  };

  return (
    <Box data-testid="widget-applied-filters-container">
      <IconButton
        data-testid="widget-applied-filters-icon"
        onClick={handleOpen}
        size="small"
        sx={{ color: isWidgetLocked ? 'text.secondary' : 'info.main' }}
        aria-label={
          isWidgetLocked
            ? t('blox.dashboards.widgets.locked_widget', {
                defaultValue: 'Locked widget',
              })
            : t('blox.dashboards.widgets.filter_info', {
                defaultValue: 'Filter information',
              })
        }
      >
        {isWidgetLocked ? (
          <LockIcon fontSize="small" />
        ) : (
          <InfoOutlinedIcon fontSize="small" />
        )}
      </IconButton>

      <Popover
        data-testid="widget-applied-filters-popover"
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 2, maxWidth: 400 }}>
          <Typography variant="body2">{renderMessage()}</Typography>
        </Box>
      </Popover>
    </Box>
  );
}
